import { useCallback } from 'react';
import { Tabs, TabsList, Tab, TabPanel } from '@spotoninc/nexus-react';
import { Flex, Text } from '@chakra-ui/react';
import FinancialOverview from '../FinancialOverview';
import Item from '../TaskDrawer/Item';
import get from 'lodash/get';
import size from 'lodash/size';

import ActionItem from '../ActionItem';
import FeedbackBlock from '../FeedbackBlock';
import InfoList from '../InfoList';
import CommentBlock from '../CommentBlock';

import styles from './summaryTaskContent.module.css';
import { useSummaryTask } from './useSummaryTask';
import { DrinksIcon, SalesIcon } from '@spotoninc/nexus-web-assets';
import { FEEDBACK_STATE } from '../FeedbackBlock/FeedbackBlock';
import DescriptionBlock from '../DescriptionBlock';

/**
 * Parent
 *      TaskDrawer
 */
function MonthlySummaryTaskContent({
  task,
  onActionItemClick,
  handleFeedbackSubmit,
  handleAssignUserChange,
}) {
  const { activeTab, handleOnChange } = useSummaryTask();

  const renderActionItems = useCallback(() => {
    if (size(task?.children) === 0)
      return (
        <Flex
          flexDirection={'column'}
          alignItems={'center'}
          gap={6}
          pt={6}
          pb={6}
          flex={1}
        >
          <Text>No action items found</Text>
        </Flex>
      );
    return (
      <Flex flexDirection={'column'} gap={6} pt={6} pb={6}>
        {task?.children.map(item => (
          <ActionItem
            key={item.id}
            task={item}
            onActionItemClick={e => {
              e?.preventDefault();
              onActionItemClick(item);
            }}
          />
        ))}
      </Flex>
    );
  }, [task?.children, onActionItemClick, handleAssignUserChange]);

  return (
    <>
      <div className="nexus-task-header">{task?.task_title}</div>
      <Item
        task={{
          status: task.status,
          priority: task.priority,
          dueDate: task.due_on || 'No due date',
          taskType: task.task_type || 'Summary',
          assignedToId: task.assigned_to_id,
          assignedUser: task.assignedUser,
          location: task?.location_label,
        }}
        handleAssignUserChange={handleAssignUserChange}
        assignmentEditable={true}
      />

      <div style={{ padding: '16px 0' }}>
        <Tabs onChange={handleOnChange} value={activeTab}>
          <TabsList className={styles.tabsList}>
            <Tab value={0} className={styles.tabs}>
              Overview
            </Tab>
            <Tab value={1} className={styles.tabs}>
              Action items
            </Tab>
          </TabsList>
          <TabPanel value={0}>
            <Flex direction={'column'} gap={4}>
              <FinancialOverview
                netIncome={{
                  amount: task?.kpi_net_income,
                }}
                revenue={{
                  amount: task?.kpi_revenue,
                  mom: task?.revenue_change_mom,
                  yoy: task?.revenue_change_yoy,
                }}
                expenses={{
                  amount: task?.kpi_expense,
                  mom: task?.expense_change_mom,
                  yoy: task?.expense_change_yoy,
                }}
              />
              <DescriptionBlock description={task?.task_desc || ''} />
              <InfoList
                IconElement={DrinksIcon}
                title="What went well this period"
                data={get(task, 'what_went_well', [])}
              />
              <InfoList
                IconElement={SalesIcon}
                title="Opportunities for Growth"
                data={get(task, 'improvement_areas', [])}
                containerClassName={styles.growthBlock}
              />
              <FeedbackBlock
                value={task?.feedback_sentiment}
                onThumbsUpClick={e => {
                  e?.preventDefault();
                  handleFeedbackSubmit(FEEDBACK_STATE.POSITIVE)();
                }}
                onThumbsDownClick={e => {
                  e?.preventDefault();
                  handleFeedbackSubmit(FEEDBACK_STATE.NEGATIVE)();
                }}
              />
              <CommentBlock comment={task?.comment} />
            </Flex>
          </TabPanel>
          <TabPanel value={1}>{renderActionItems()}</TabPanel>
        </Tabs>
      </div>
    </>
  );
}

export default MonthlySummaryTaskContent;
